import { useQuery } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/api/client";
import { type FlowRunDagResponse, nextRefetchInterval } from "@/api/flow-run-dag";
import type { LifecycleStateLabel, TimingSource } from "@/api/projects";
import type { FlowRunFeedSummary } from "@/api/runs";

// One flow run — one submission of a flow template — read by its own id
// (FLOW_RUN_PAGE_PLAN C1/C2). Wire shapes mirror `FlowRunDetailResponse`,
// `FlowRunStep` and `FlowRunOutput` in src/flowstack/server/routes/flow_runs.py.

/** The id prefix `POST /api/flows/submit` mints (`flow-run-` + a token). A
 * node run's id looks like `flowrun-<node>-<hash>` — no hyphen after `flow` —
 * so this test cannot claim one. */
export const FLOW_RUN_ID_PREFIX = "flow-run-";

/** Whether `/runs/<id>` names a flow run rather than one of its steps (C4). */
export function isFlowRunId(id: string): boolean {
  return id.startsWith(FLOW_RUN_ID_PREFIX);
}

/** Why a flow run settled `failed`, as structured fields. Mirrors
 * `FlowRunFailureReason` in `lifecycle/flow_run.py`; `message` is present only
 * when the raising error opted in to showing it, and `error_code` only for
 * `spending_refused` (fs#2179). */
export interface FlowRunFailureReason {
  kind:
    | "run_failed"
    | "refused_pre_dispatch"
    | "unresolved_bindings"
    | "expansion_failed"
    | "frontier_stuck"
    | "spending_refused";
  node: string | null;
  scope: [string, number][] | null;
  error_type: string | null;
  message: string | null;
  run_id: string | null;
  run_state: string | null;
  /** Absent from an export built before fs#2179. */
  error_code?:
    | "monthly_cap"
    | "per_run_cap"
    | "insufficient_funds"
    | "wallet_closed"
    | "quote_unavailable"
    | "max_concurrent_runs"
    | null;
}

/** One step: a node run stamped with this flow run's id. */
export interface FlowRunStep {
  run_id: string;
  flow_node_name: string | null;
  tile_name: string;
  status: LifecycleStateLabel;
  cache_hit: boolean;
  submitted_at: string;
  /** The window `GET /api/runs/{run_id}` reports; null means not recorded. */
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  /** null while neither clock holds both ends (queued, or served from cache). */
  timing_source: TimingSource | null;
}

/** One artifact a terminal step produced, or was served from the cache. */
export interface FlowRunOutput {
  port: string;
  content_hash: string;
  typed_object: string;
  producer_run_id: string;
  flow_node_name: string | null;
  served_from_cache: boolean;
}

/**
 * `GET /api/flow-runs/{flow_run_id}`. The header is the same rollup the Runs
 * list carries for this flow run, plus the fields only the flow-run record
 * holds; `status`, `flow_version` and `failure_reason` are null when there is
 * no such record (the steps alone are rendered then).
 */
export interface FlowRunDetail extends FlowRunFeedSummary {
  flow_version: string | null;
  failure_reason: FlowRunFailureReason | null;
  project_slug: string | null;
  /** Every step, oldest first. */
  steps: FlowRunStep[];
  /** The terminal steps' outputs; empty when `outputs_unavailable_reason` is set. */
  outputs: FlowRunOutput[];
  outputs_unavailable_reason: string | null;
}

/** Still moving: a `running` flow run, or — with no flow-run record to say —
 * one whose counted steps have not all settled. */
export function flowRunIsLive(flowRun: FlowRunFeedSummary): boolean {
  if (flowRun.status !== null) return flowRun.status === "running";
  return flowRun.steps_settled < flowRun.steps_total;
}

export function flowRunQueryKey(flowRunId: string): readonly unknown[] {
  return ["flow-runs", "detail", flowRunId] as const;
}

/** One flow run, polled every 5s while it is live (the cadence `useRun` uses). */
export function useFlowRun(flowRunId: string) {
  return useQuery({
    queryKey: flowRunQueryKey(flowRunId),
    queryFn: async (): Promise<FlowRunDetail> =>
      apiFetch<FlowRunDetail>(`/api/flow-runs/${encodeURIComponent(flowRunId)}`),
    staleTime: 10_000,
    enabled: Boolean(flowRunId),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && flowRunIsLive(data) ? 5_000 : false;
    },
  });
}

export function flowRunDagByIdQueryKey(flowRunId: string): readonly unknown[] {
  return ["flow-runs", "flow-dag", flowRunId] as const;
}

/**
 * `GET /api/flow-runs/{flow_run_id}/flow-dag` (C2): the same body as
 * `useFlowRunDag` reads for one of the flow run's steps, keyed by the flow run
 * itself, so a flow run with no step yet still has its graph. Polls and
 * retries exactly as `useFlowRunDag` does.
 */
export function useFlowRunDagById(flowRunId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && Boolean(flowRunId);
  return useQuery({
    queryKey: flowRunDagByIdQueryKey(flowRunId),
    queryFn: async (): Promise<FlowRunDagResponse> =>
      apiFetch<FlowRunDagResponse>(`/api/flow-runs/${encodeURIComponent(flowRunId)}/flow-dag`),
    enabled,
    staleTime: 4_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
    refetchInterval: (query) =>
      nextRefetchInterval({
        status: query.state.status,
        dataUpdatedAt: query.state.dataUpdatedAt,
        summaryState: query.state.data?.summary.state,
      }),
  });
}
